import { ButtonType } from 'inquiry/components/ButtonBase';
import { ClientSideCollectedData } from 'inquiry/lib/types';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import {
  BaseStepStyles,
  GovernmentIdClassLocalizations,
  SerializedBaseStepStyles,
  SerializedButtonStyles,
  SerializedStepDocumentStyles,
  SerializedStepGovernmentIdStyles,
  SerializedStepSelfieStyles,
  StepDocumentConfig,
  StepDocumentStyles,
  StepGovernmentIdConfig,
  StepGovernmentIdStyles,
  StepSelfieConfig,
  StepSelfieStyles,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import { RequiredDevice } from 'lib/interfaces/verificationConfig';
import { isMacSafari, supportsInputCapture } from 'lib/utils/device';
import { assertNever } from 'lib/utils/types';
import { FromWidgetEvent, HolderClient } from 'verify/lib/clients/HolderClient';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
  GovernmentIdCaptureOptionDesktop,
  GovernmentIdCaptureOptionMobile,
  SelfieCaptureOptionDesktop,
  SelfieCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import { Inquiry, InquiryStatus } from 'verify/models/inquiry';

export const getSupportedGovernmentIdCaptureOptions = (
  config: StepGovernmentIdConfig,
  governmentIdClass: GovernmentIdClass,
  hasWebCamera: boolean,
): Array<GovernmentIdCaptureOptionDesktop | GovernmentIdCaptureOptionMobile> => {
  const {
    'enabled-capture-options-desktop': enabledCaptureOptionsDesktop,
    'enabled-capture-options-mobile': enabledCaptureOptionsMobile,
    idclasses,
  } = config;

  const isMobile = supportsInputCapture();
  const enabledCaptureOptions = isMobile
    ? enabledCaptureOptionsMobile
    : enabledCaptureOptionsDesktop;

  const idSetup = idclasses.find((v) => v.class === governmentIdClass);
  // "Other" gov ID (which we don't verify) doesn't have an ID setup, so mobile isn't needed.
  const noMobileRequirement = idSetup?.['requires-device'] !== RequiredDevice.Mobile;
  // Mac safari (not chrome) actually has good enough resolution.
  const sufficientCameraQuality = noMobileRequirement || isMacSafari() || isMobile;

  const showWebCameraOption =
    hasWebCamera &&
    // Barcode scan requires mobile device-level cameras
    sufficientCameraQuality &&
    // Checked separately to ensure native camera is turned off
    enabledCaptureOptions.includes('web_camera');

  // mobile_camera is only shown if user is on mobile and any of the following are true:
  // 1. web_camera is disabled
  // 2. web_camera is enabled but user's device is not good enough
  // 3. always-show-mobile-camera-capture-option is true
  const showMobileCameraOption =
    isMobile && (!showWebCameraOption || config['always-show-mobile-camera-capture-option']);

  // For some reason, the FileReader API is not always available in WkWebView.
  const supportsFileUpload = window.FileReader != null;

  // These are listed in the order they appear (top to bottom on page)
  // The server is responsible for maintaining sort order.
  return enabledCaptureOptions.filter((option) => {
    switch (option) {
      case 'web_camera':
        return showWebCameraOption;
      case 'mobile_camera':
        return showMobileCameraOption;
      case 'upload':
        return supportsFileUpload;
      case 'device_handoff':
        // mobile can show device handoff if enabled
        return true;
      default:
        assertNever(option);
    }
  });
};

export const getSupportedSelfieCaptureOptions = ({
  config,
  isWebGLIncompatible,
  hasCamera,
}: {
  config: StepSelfieConfig;
  isWebGLIncompatible: boolean;
  hasCamera: boolean;
}): Array<SelfieCaptureOptionDesktop | SelfieCaptureOptionMobile> => {
  const {
    'enabled-capture-options-desktop': enabledCaptureOptionsDesktop,
    'enabled-capture-options-mobile': enabledCaptureOptionsMobile,
  } = config;

  const isMobile = supportsInputCapture();
  const enabledCaptureOptions = isMobile
    ? enabledCaptureOptionsMobile
    : enabledCaptureOptionsDesktop;

  const showWebCameraOption =
    hasCamera && !isWebGLIncompatible && enabledCaptureOptions.includes('web_camera');

  // These are listed in the order they appear (top to bottom on page)
  // The server is responsible for maintaining sort order.
  return enabledCaptureOptions.filter((option) => {
    switch (option) {
      case 'web_camera':
        return showWebCameraOption;
      case 'device_handoff':
        // mobile can show device handoff if enabled
        return true;
      default:
        assertNever(option);
    }
  });
};

export const getSupportedDocumentCaptureOptions = (
  config: StepDocumentConfig,
  hasWebCamera: boolean,
): Array<DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile> => {
  const {
    'enabled-capture-options-desktop': enabledCaptureOptionsDesktop,
    'enabled-capture-options-mobile': enabledCaptureOptionsMobile,
  } = config;

  const isMobile = supportsInputCapture();
  const enabledCaptureOptions = isMobile
    ? enabledCaptureOptionsMobile
    : enabledCaptureOptionsDesktop;

  const showWebCameraOption = hasWebCamera && enabledCaptureOptions?.includes('web_camera');

  // These are listed in the order they appear (top to bottom on page)
  // The server is responsible for maintaining sort order.
  return enabledCaptureOptions.filter((option) => {
    switch (option) {
      case 'web_camera':
        return showWebCameraOption;
      case 'mobile_camera':
        return !showWebCameraOption && isMobile;
      case 'upload':
        return true;
      case 'device_handoff':
        // mobile can show device handoff if enabled
        return true;
      default:
        assertNever(option);
    }
  });
};

export const isTerminalStep = (inquiry: Inquiry): boolean => {
  const { terminal } = inquiry.attributes['next-step'].config;

  return terminal;
};

/**
 * If country code not passed, check for unscoped translation for ID class.
 *
 * If country code is passed, first check for country code scoped translation,
 * and fall back to unscoped.
 *
 * This is only relevant for us-id for now. We may need to support other cases
 * of {country}-{idclass} in the future.
 */
export function textForGovernmentId(
  localizations: GovernmentIdClassLocalizations | undefined,
  countryCode: CountryCode | undefined = undefined,
  governmentIdClass: GovernmentIdClass,
) {
  const textWithoutCountryScoping =
    localizations?.[governmentIdClass as keyof typeof localizations];

  if (countryCode == null) {
    return textWithoutCountryScoping;
  } else {
    const textWithCountryScoping =
      localizations?.[
        `${countryCode.toLowerCase()}-${governmentIdClass}` as keyof typeof localizations
      ];

    return textWithCountryScoping ?? textWithoutCountryScoping;
  }
}

/**
 * Callback to client with results from inquiry. Triggers the success event
 * if on a terminal step.
 * step.
 */
export const sendCompletionEvent = (
  inquiry: Inquiry,
  collectedData?: ClientSideCollectedData[],
) => {
  if (!isTerminalStep(inquiry)) {
    throw new Error('Cannot trigger completion on non-terminal step');
  }

  HolderClient.onEvent(
    FromWidgetEvent.Complete,
    // TODO: make this typesafe. See HolderClient.onEvent()
    {
      inquiryId: inquiry.id,
      referenceId: inquiry.attributes['reference-id'],
      status: inquiry.attributes.status,
      fields: inquiry.attributes.fields,
      collectedData: HolderClient.inquiryParams['send-client-side-collected-data-in-events']
        ? collectedData
        : undefined,
    },
    { inquiry },
  );
};

export const serializeBaseStepStyles = (
  styles?: BaseStepStyles | null,
): SerializedBaseStepStyles => {
  if (styles == null) {
    return {
      base: undefined,
      buttons: undefined,
    };
  }

  return {
    base: {
      backgroundColor: styles['background-color'].base.base,
      headerButton: {
        text: {
          color: styles['text-color']['header-button']['base'],
        },
      },
      header: {
        border: {
          color: styles['border-color'].header.base,
          width: styles['border-width'].header.base,
        },
      },
      modal: {
        backgroundColor: styles['background-color'].base.base,
        padding: styles.padding.modal.base,
        border: {
          radius: styles['border-radius'].modal.base,
        },
      },
      fill: {
        color: styles['fill-color'].base.base,
        // FIXME: remove optional access once backend change to add these attributes is deployed
        highlightColor: styles['fill-color'].highlight?.base,
        backgroundColor: styles['fill-color'].background?.base,
      },
      stroke: {
        color: styles['stroke-color'].base.base,
      },
      textStyle: styles['text-style'].base.base,
      titleStyle: styles['title-style'].base.base,
      disclaimerStyle: styles['disclaimer-style'].base.base,
    },
    buttons: serializeButtonStyles(styles),
  };
};

const serializeButtonStyles = (styles?: BaseStepStyles | null): SerializedButtonStyles => {
  if (styles == null) {
    return {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [ButtonType.Primary]: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [ButtonType.Secondary]: null,
    };
  }

  return {
    [ButtonType.Primary]: styles['button-primary-style'].base.base,
    [ButtonType.Secondary]: styles['button-secondary-style'].base.base,
  };
};

export const serializeStepDocumentStyles = (
  styles?: StepDocumentStyles | null,
): SerializedStepDocumentStyles => {
  if (styles == null) {
    return {
      base: undefined,
      buttons: undefined,
      imagePreview: undefined,
      processingPage: undefined,
    };
  }

  const { base, buttons } = serializeBaseStepStyles(styles);

  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    base: {
      ...base,
      border: {
        color: styles['border-color'].base,
        width: styles['border-width'].base,
      },
    },
    buttons,
    imagePreview: {
      border: {
        color: styles['border-color']['image-preview'].base,
        radius: styles['border-radius']['image-preview'].base,
        width: styles['border-width']['image-preview'].base,
      },
      plusIcon: {
        stroke: {
          color: styles['stroke-color']['image-preview-plus-icon'].base,
        },
        fill: {
          color: styles['fill-color']['image-preview-plus-icon'].base,
        },
      },
      xIcon: {
        stroke: {
          color: styles['stroke-color']['image-preview-x-icon'].base,
        },
        fill: {
          color: styles['fill-color']['image-preview-x-icon'].base,
        },
      },
      mainArea: {
        fill: {
          color: styles['fill-color']['image-preview-main-area'].base,
        },
      },
      cropArea: {
        fill: {
          color: styles['fill-color']['image-preview-crop-area'].base,
        },
      },
      imageName: {
        textStyle: styles['text-style']['image-name'].base,
      },
    },
    processingPage: {
      textStyle: styles['text-style']['document-processing-text'].base,
      titleStyle: styles['title-style']['document-processing-title'].base,
    },
  };
};

export const serializeStepGovernmentIdStyles = (
  styles?: StepGovernmentIdStyles | null,
): SerializedStepGovernmentIdStyles => {
  if (styles == null) {
    return {
      base: undefined,
      buttons: undefined,
      captureHint: undefined,
      captureHintIcon: undefined,
      captureButtons: undefined,
      captureFeedBox: undefined,
      captureOverlayHeaderButton: undefined,
      processingPage: undefined,
      reviewImageBox: undefined,
      selectOption: undefined,
    };
  }

  const { base, buttons } = serializeBaseStepStyles(styles);

  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    base: {
      ...base,
      border: {
        color: styles['border-color'].base,
        width: styles['border-width'].base,
      },
    },
    buttons,
    captureHint: {
      textStyle: styles['text-style']['government-id-capture-hint-text'].base,
    },
    captureHintIcon: {
      fill: {
        color:
          // Mobile uses capture-page-hint-icon but web used base. Fall back to base to avoid a breaking change.
          styles['fill-color']['capture-page-hint-icon']?.base ??
          styles['fill-color']['base']?.base,
      },
      stroke: {
        color:
          // Mobile uses capture-page-hint-icon but web used base. Fall back to base to avoid a breaking change.
          styles['stroke-color']['capture-page-hint-icon']?.base ??
          styles['stroke-color']['base']?.base,
      },
    },
    captureButtons: {
      submit: styles['button-primary-style']['submit-photo-button']?.base,
      retake: styles['button-secondary-style']['retake-photo-button']?.base,
    },
    captureHelpButtonIcon: {
      stroke: {
        color: styles['stroke-color']['capture-page-help-button-icon']?.base,
      },
    },
    captureFeedBox: {
      border: {
        color: styles['border-color']['government-id-capture-feed-box'].base,
        radius: styles['border-radius']['government-id-capture-feed-box'].base,
        width: styles['border-width']['government-id-capture-feed-box'].base,
      },
      stroke: {
        color: styles['stroke-color']['government-id-capture-feed-box']?.base,
      },
    },
    captureOverlayHeaderButton: {
      stroke: {
        color: styles['stroke-color']['capture-overlay-header-button']?.base,
      },
    },
    processingPage: {
      textStyle: styles['text-style']['government-id-processing-text'].base,
      titleStyle: styles['title-style']['government-id-processing-title'].base,
    },
    reviewImageBox: {
      border: {
        color: styles['border-color']['government-id-review-image-box'].base,
        radius: styles['border-radius']['government-id-review-image-box'].base,
        width: styles['border-width']['government-id-review-image-box'].base,
      },
    },
    selectOption: {
      backgroundColor: {
        base: styles['background-color'].base.base,
        active: styles['background-color'].option.active,
        focused: styles['background-color'].option.focused,
      },
      border: {
        color: styles['border-color']['government-id-select-option'].base,
        width: styles['border-width']['government-id-select-option'].base,
      },
      chevron: {
        stroke: {
          color: styles['stroke-color']['government-id-select-option-chevron'].base,
        },
      },
      height: styles['height']['government-id-select-option-min-row-height'].base,
      icon: {
        stroke: {
          color: styles['stroke-color']['government-id-select-option-icon'].base,
        },
        fill: {
          color: styles['fill-color']['government-id-select-option-icon'].base,
        },
      },
      textStyle: styles['text-style']['government-id-vertical-option'].base,
    },
    inputSelect: {
      base: styles['input-select-style']['base'].base,
    },
    imageLocal: {
      illustration: styles['image-local-style']['illustration'].base,
    },
  };
};

export const serializeStepSelfieStyles = (
  styles?: StepSelfieStyles | null,
): SerializedStepSelfieStyles => {
  if (styles == null) {
    return {
      base: undefined,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      buttons: undefined,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      captureFeedbox: undefined,
      captureHintText: undefined,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      captureCompleteIcon: undefined,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      captureIcon: undefined,
      processingPage: undefined,
      startIcon: undefined,
    };
  }

  const { base, buttons } = serializeBaseStepStyles(styles);

  return {
    base,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    buttons,
    captureFeedbox: {
      border: {
        color: styles['border-color']['selfie-capture-feed-box'].base,
        width: styles['border-width']['selfie-capture-feed-box'].base,
      },
    },
    /**
     * FIXME: This should use selfie-capture-hint-text instead of
     * selfie-processing-text, but due to a web/mobile discrepancy, that
     * defaults to white. That breaks the web flow, so we use the success text
     * color, which defaults to black.
     */
    captureHintText: styles['text-style']['selfie-processing-text'].base,
    captureCompleteIcon: {
      stroke: {
        color: styles['stroke-color']['capture-complete-icon'].base,
      },
    },
    captureIcon: {
      fill: {
        color: styles['fill-color']['selfie-capture-icon-fill-color'].base,
      },
      background: {
        color: styles['fill-color']['selfie-capture-icon-background-fill-color'].base,
      },
      stroke: {
        color: styles['stroke-color']['selfie-capture-icon-stroke-color'].base,
      },
    },
    processingPage: {
      titleStyle: styles['title-style']['selfie-processing-title'].base,
      textStyle: styles['text-style']['selfie-processing-text'].base,
    },
    startIcon: styles['image-local-style']['selfie-start-icon'].base,
  };
};

export function isFinalFlowStep(inquiry: Inquiry): boolean {
  if (
    inquiry.attributes['next-step'].type === UiStepType.Completed ||
    inquiry.attributes['next-step'].type === UiStepType.Failed
  ) {
    return true;
  }

  if (!inquiry.attributes.status) {
    return false;
  }

  return [
    // Terminal states during flow
    InquiryStatus.Completed,
    InquiryStatus.Failed,
    // Review terminal states
    InquiryStatus.Approved,
    InquiryStatus.Declined,
    InquiryStatus.NeedsReview,
  ].includes(inquiry.attributes.status);
}
